import React, { useCallback, memo, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ButtonOld from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { getATLContextUrl, isFedRamp } from '@atlassian/atl-context';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import Tools from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/tools';
import JiraBoard from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/components/jira-board';
import ChatEmoji from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/chat-emojis';
import LightBulbOrbit from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/lightbulb-orbit';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import {
	JSM_BOARD,
	SIMILAR_ISSUES_FEATURE,
	SENTIMENT_FEATURE,
	AUTOMATICALLY_SET_REQUEST_TYPE_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants';
import AsyncDefaultRequestTypeSettings from '@atlassian/jira-servicedesk-default-request-type-settings/src/async';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { ExternalLink } from '../../../../common/ui/external-link';
import { FeatureSection } from '../../../../common/ui/feature-section/index.tsx';
import { FeatureToggleTile } from '../../../../common/ui/feature-toggle-tile/index.tsx';
import useAdminHubAIEnabled from '../../../../controllers/common/ai-enablement-controller/index.tsx';
import useSaveFailedFlag from '../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../controllers/common/service-project-features-controller/index.tsx';
import messages from './messages';

const renderGroupTitle = (message: string) => {
	const headingLevel = fg('jsmca11y-564-update-section-headings') ? 'h3' : 'h4';
	return (
		<Heading size="xsmall" as={headingLevel}>
			<GroupTitle>{message}</GroupTitle>
		</Heading>
	);
};

export const ADMIN_HUB_URL: Record<Environment, string> = {
	prod: getATLContextUrl('admin'),
	dev: 'https://admin.stg.atlassian.com',
	local: 'https://admin.stg.atlassian.com',
	staging: 'https://admin.stg.atlassian.com',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LabsSection = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const isAdminHubAiToggleEnabled = useAdminHubAIEnabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: projectContextData } = useProjectContext();
	const { data: orgId } = useOrgId();
	const environment = useEnvironment();
	const isSimplifiedProject = Boolean(projectContextData?.simplified);

	const [showDefaultRequestTypeSettings, setShowDefaultRequestTypeSettings] =
		useState<boolean>(false);

	const onDefaultRequestTypeSettingsClick = useCallback(() => {
		setShowDefaultRequestTypeSettings(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'openDefaultRequestTypeModal');
	}, [setShowDefaultRequestTypeSettings, createAnalyticsEvent]);

	const createOnChangeHandler = useCallback(
		(featureTitle: string) =>
			(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticEvent, 'settingsToggle', {
					field: feature,
					toggleStatus: enabled,
				});
				update({
					feature,
					enabled,
					onFail: () => {
						showSaveErrorFlag(featureTitle, enabled);
					},
				});
			},
		[showSaveErrorFlag, update],
	);

	const createOnDefaultRequestTypeChangeHandler = useCallback(
		(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
			if (ff('jsm-default-request-types-none-option_4mnti')) {
				if (enabled) {
					setShowDefaultRequestTypeSettings(true);
				}
			}
			createOnChangeHandler(formatMessage(messages.defaultRequestTypesTitle))(
				feature,
				enabled,
				analyticEvent,
			);
		},
		[createOnChangeHandler, formatMessage],
	);

	const createLearnMoreLink = (
		href: string,
		message: string,
		articleId: string,
		eventName: string,
	) => {
		const learnMoreLinkButton = <ExternalLink href={href}>{message}</ExternalLink>;
		return (
			<HelpPanelLink
				articleId={articleId}
				appearance="link"
				spacing="none"
				fallbackComponent={learnMoreLinkButton}
				onClick={() =>
					fireUIAnalytics(createAnalyticsEvent({}), 'helpLink clicked', eventName, {
						articleId,
					})
				}
			>
				{message}
			</HelpPanelLink>
		);
	};

	const learnMoreAboutLabsDocs = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/what-is-the-feature-lab/',
		formatMessage(messages.labsDocsLink),
		'3CH7d7kzG4SQH1kOzy8upV',
		'learnMoreAboutFeatureLab',
	);

	const learnMoreAboutSimilarRequests = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-similar-requests/',
		formatMessage(messages.similarRequestsLink),
		'4Ok9cNp3kIzh05dO9uQTQj',
		'learnMoreAboutSimilarRequestPanel',
	);

	const learnMoreAboutSentimentField = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/sentiment-analysis-using-atlassian-intelligence/',
		formatMessage(messages.sentimentLink),
		'15BayucZiHGq52j608lgaE',
		'learnMoreAboutSimilarRequestPanel',
	);

	const learnMoreAboutAutomaticallySettingRequestTypes = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/choose-default-request-types-for-your-issue-types/',
		formatMessage(messages.defaultRequestTypesLink),
		'64ZIz1OHI0cPxVGYUEKQ0K',
		'learnMoreAboutAutomaticallySettingRequestTypes',
	);

	const learnMoreAboutJsmBoards = createLearnMoreLink(
		'https://support.atlassian.com/jira-service-management-cloud/docs/manage-your-work-using-a-board/',
		formatMessage(messages.jsmBoardLink),
		'103YhAB4GHpQvY8MAc0Q2r',
		'learnMoreAboutJsmBoards',
	);

	const adminHubLink = `${ADMIN_HUB_URL[environment]}/o/${orgId}/atlassian-intelligence`;

	const sentimentFeaturePrerequisiteTooltip = (
		<FormattedMessage
			{...messages.sentimentFeaturePrerequisiteTooltip}
			values={{
				link: (chunks) => (
					<a onMouseDown={() => window.open(adminHubLink)} target="_blank" href={adminHubLink}>
						{chunks}
					</a>
				),
			}}
		/>
	);

	return (
		<FeatureSection
			title={formatMessage(messages.labsSectionTitle)}
			description={
				<FormattedMessage
					{...messages.labsSectionDescription}
					values={{
						learnMoreLink: learnMoreAboutLabsDocs,
					}}
				/>
			}
		>
			{!ff('jsm-feature-labs-views-extraction_3odmp') && (
				<>
					{renderGroupTitle(formatMessage(messages.jsmBoardGroupTitle))}
					<FeatureToggleTile
						feature={JSM_BOARD}
						title={formatMessage(messages.jsmBoardTitle)}
						badge={
							ff('jsm-kanban-board-lozenge_31x91') && (
								<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
							)
						}
						illustration={<JiraBoard alt="" />}
						value={data?.[JSM_BOARD]?.status === ENABLED}
						saving={saving[JSM_BOARD]}
						onChange={createOnChangeHandler(formatMessage(messages.jsmBoardTitle))}
					>
						{formatMessage(messages.jsmBoardDescription, {
							learnMoreLink: learnMoreAboutJsmBoards,
						})}
					</FeatureToggleTile>
				</>
			)}
			{!isSimplifiedProject && renderGroupTitle(formatMessage(messages.issueViewTitle))}
			{!isFedRamp() && (
				<FeatureToggleTile
					feature={SIMILAR_ISSUES_FEATURE}
					title={formatMessage(messages.similarRequestsPanelTitle)}
					value={data?.[SIMILAR_ISSUES_FEATURE]?.status === ENABLED}
					saving={saving[SIMILAR_ISSUES_FEATURE]}
					onChange={createOnChangeHandler(formatMessage(messages.similarRequestsPanelTitle))}
					illustration={<LightBulbOrbit alt="" />}
				>
					<FormattedMessage
						{...messages.similarRequestsPanelDescription}
						values={{
							learnMoreLink: learnMoreAboutSimilarRequests,
						}}
					/>
				</FeatureToggleTile>
			)}

			{!isSimplifiedProject && (
				<FeatureToggleTile
					feature={AUTOMATICALLY_SET_REQUEST_TYPE_FEATURE}
					title={formatMessage(messages.defaultRequestTypesTitle)}
					value={data?.[AUTOMATICALLY_SET_REQUEST_TYPE_FEATURE]?.status === ENABLED}
					saving={saving[AUTOMATICALLY_SET_REQUEST_TYPE_FEATURE]}
					onChange={createOnDefaultRequestTypeChangeHandler}
					illustration={<Tools alt="" />}
				>
					<FormattedMessage
						{...messages.defaultRequestTypesDescription}
						values={{
							learnMoreLink: learnMoreAboutAutomaticallySettingRequestTypes,
						}}
					/>
					<Box
						xcss={fg('jsm_views_of_work_phase_1') ? buttonWrapperStyles : buttonWrapperStylesOld}
					>
						{fg('jsm_views_of_work_phase_1') ? (
							<Button iconBefore={SettingsIcon} onClick={onDefaultRequestTypeSettingsClick}>
								{formatMessage(messages.defaultRequestTypeSettingsButtonText)}
							</Button>
						) : (
							<ButtonOld
								appearance="link"
								iconBefore={<SettingsIcon label="" />}
								onClick={onDefaultRequestTypeSettingsClick}
							>
								{formatMessage(messages.defaultRequestTypeSettingsButtonText)}
							</ButtonOld>
						)}
						{showDefaultRequestTypeSettings && (
							<AsyncDefaultRequestTypeSettings
								onClose={() => setShowDefaultRequestTypeSettings(false)}
							/>
						)}
					</Box>
				</FeatureToggleTile>
			)}
			<FeatureToggleTile
				feature={SENTIMENT_FEATURE}
				title={formatMessage(messages.sentimentFieldTitle)}
				value={data?.[SENTIMENT_FEATURE]?.status === ENABLED}
				saving={saving[SENTIMENT_FEATURE]}
				onChange={createOnChangeHandler(formatMessage(messages.sentimentFieldTitle))}
				aiFooterRequired
				tooltip={sentimentFeaturePrerequisiteTooltip}
				disabled={!isAdminHubAiToggleEnabled}
				illustration={<ChatEmoji alt="" />}
			>
				<FormattedMessage
					{...messages.sentimenFieldDescription}
					values={{
						learnMoreLink: learnMoreAboutSentimentField,
					}}
				/>
			</FeatureToggleTile>
		</FeatureSection>
	);
});

export default LabsSection;

const buttonWrapperStylesOld = xcss({
	marginTop: 'space.100',
	marginLeft: 'space.negative.200',
});

const buttonWrapperStyles = xcss({
	marginTop: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupTitle = styled2.span({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N90),
	marginBottom: token('space.negative.100', '-8px'),
});
